import React from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    Divider,
    Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

const SubscriptionBilling = () => {
    const standardFeatures = [
        '30 contacts per month',
        'Screen resumes with AI- assistance',
        'Access a global pool of candidates'
    ];

    const professionalFeatures = [
        '100 contacts per month',
        'Get access to candidate engagement insights',
        'Improve team collaboration with shared candidate lists',
        'Save time with bulk messaging'
    ];

    return (
        <Box>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600, mb: 4, color: 'text.primary',
                    fontSize: { xs: '20px', md: '24px' }
                }}
            >
                Subscription & Billing
            </Typography>

            <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                {/* Standard Plan */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0}
                        sx={{
                            p: 4, borderRadius: 3, bgcolor: '#F5F5F5', height: '87.2%',
                            display: 'flex', flexDirection: 'column',
                            // border: '1px solid', borderColor: 'divider'
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        bgcolor: 'white', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        border: '1px solid', borderColor: 'divider'
                                    }}
                                >
                                    <PersonOutlineIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h5"
                                        sx={{
                                            fontWeight: 700, color: 'text.primary', fontSize: { xs: '20px', md: '24px' },
                                            lineHeight: 1.2
                                        }}
                                    >
                                        Standard
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', mt: 0.5 }} >
                                        Occasional hiring
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Pricing */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        fontSize: { xs: '32px', md: '40px' },
                                        lineHeight: 1
                                    }}
                                >
                                    ₹8,000
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '14px'
                                    }}
                                >
                                    per month*
                                </Typography>
                            </Box>
                        </Box>

                        {/* Buy Now Button */}
                        <Button variant="outlined"
                            fullWidth
                            sx={{
                                mb: 3, py: 1.5,
                                borderRadius: '8px', borderColor: '#6B31C4',
                                color: '#6B31C4', fontWeight: 600,
                                fontSize: '14px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#6B31C4',
                                    bgcolor: '#6B31C4',
                                    color: 'white'
                                }
                            }}
                        >
                            Buy Now
                        </Button>

                        {/* Divider */}
                        <Divider sx={{ mb: 3 }} />

                        {/* Features */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: '16px',
                                    mb: 2
                                }}
                            >
                                Included features
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {standardFeatures.map((feature, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 20,
                                                color: '#EF4444',
                                                mt: 0.25,
                                                flexShrink: 0
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.primary',
                                                fontSize: '14px',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {feature}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Professional Plan */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={0}
                        sx={{
                            p: 4, borderRadius: 3, background: 'linear-gradient(180deg, #1265FA 0%, #053997 100%)',
                            // height: '100%',
                            display: 'flex', flexDirection: 'column', position: 'relative'
                        }}
                    >


                        {/* Header */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 50, height: 50, borderRadius: '70px',
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                >
                                    <GroupsIcon sx={{ fontSize: 24, color: 'white' }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h5"
                                        sx={{
                                            fontWeight: 700, color: 'white',
                                            fontSize: { xs: '20px', md: '24px' }, lineHeight: 1.2
                                        }}
                                    >
                                        Professional
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px' }}  >
                                            Continual hiring
                                        </Typography>
                                        {/* Popular Badge */}
                                        <Chip icon={<LocalFireDepartmentIcon sx={{ fontSize: 16, color: 'white' }} />} label="Popular"
                                            sx={{
                                                bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', mr: 1, fontWeight: 600, fontSize: '12px', height: 28,
                                                '& .MuiChip-icon': { color: 'white' }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Pricing */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '32px', md: '36px' }, lineHeight: 1 }}    >
                                    ₹15,000
                                </Typography>
                                <Typography variant="body2" sx={{
                                    color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px'
                                }}
                                >
                                    per month*
                                </Typography>
                            </Box>
                        </Box>

                        {/* Buy Now Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{
                                mb: 3, py: 1.5,
                                borderRadius: '8px', bgcolor: 'white',
                                color: '#3B82F6',
                                fontWeight: 600,
                                fontSize: '14px', textTransform: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                                }
                            }}
                        >
                            Buy Now
                        </Button>

                        {/* Divider */}
                        <Divider
                            sx={{
                                mb: 3,
                                borderColor: 'rgba(255, 255, 255, 0.2)'
                            }}
                        />

                        {/* Features */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    color: 'white',
                                    fontSize: '16px',
                                    mb: 2
                                }}
                            >
                                Everything in standard, plus
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {professionalFeatures.map((feature, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 20,
                                                color: 'white',
                                                mt: 0.25,
                                                flexShrink: 0
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'white',
                                                fontSize: '14px',
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {feature}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SubscriptionBilling;

